import {
  LogicNameFilter,
  Platforms,
  ServerOS,
} from "../../../../../utils/constants";
import { getHostLastSeenTimestamp } from "../../../../../utils/search";
import { getTimestampXDaysAgo } from "../../../../../utils/timestamp";
import {
  getMacOSName,
  getWindowsOSName,
} from "../../components/entitiesCharts/hostChart/osVersionDistribution/osVersionHelpers";
import { ALL_OPTION_NAME, filterName, pastTimeValues } from "../utils";

export const applyHostFilters = (
  entities,
  appliedFilters,
  filterAndOrLogics
) => {
  let filteredEntities = [...entities];

  // hostOS
  if (
    filterName.HOST_OS in appliedFilters &&
    Object.keys(appliedFilters[filterName.HOST_OS]).length > 0
  ) {
    const checkedOSs = Object.keys(appliedFilters[filterName.HOST_OS]).filter(
      (key) => appliedFilters[filterName.HOST_OS][key]
    );
    if (checkedOSs.length > 0) {
      filteredEntities = filteredEntities.filter((entity) => {
        // Check if Windows Server is selected
        if (checkedOSs.includes(ServerOS.WINDOWS_SERVER)) {
          if (entity.server_os === ServerOS.WINDOWS_SERVER) {
            return true;
          }
        }

        // Check if regular Windows is selected
        if (checkedOSs.includes(Platforms.WINDOWS)) {
          if (
            entity.platform === Platforms.WINDOWS &&
            entity.server_os !== ServerOS.WINDOWS_SERVER
          ) {
            return true;
          }
        }

        // Check other platforms (non-Windows)
        const otherOSs = checkedOSs.filter(
          (os) => os !== ServerOS.WINDOWS_SERVER && os !== Platforms.WINDOWS
        );
        if (otherOSs.includes(entity.platform)) {
          return true;
        }

        return false;
      });
    }
  }

  // hostOSFullVersion
  const availableOSVersionsAfterHostOSFilter = Array.from(
    new Set(filteredEntities.map((entity) => entity.os_version))
  );
  const availableOSVersionsNames = Array.from(
    new Set(
      filteredEntities.map((entity) =>
        entity.platform === Platforms.WINDOWS
          ? getWindowsOSName(entity.os_version)
          : entity.platform === Platforms.MACOS
          ? getMacOSName(entity.os_version)
          : entity.os_version
      )
    )
  );

  if (filterName.HOST_OS_FULL_VERSION in appliedFilters) {
    for (const OSVersion of Object.keys(
      appliedFilters[filterName.HOST_OS_FULL_VERSION]
    )) {
      if (
        OSVersion !== ALL_OPTION_NAME &&
        !availableOSVersionsAfterHostOSFilter.includes(OSVersion)
      ) {
        delete appliedFilters[filterName.HOST_OS_FULL_VERSION][OSVersion];
      }
    }
  }

  if (
    filterName.HOST_OS_FULL_VERSION in appliedFilters &&
    Object.keys(appliedFilters[filterName.HOST_OS_FULL_VERSION]).length > 0
  ) {
    const checkedOSVersions = Object.keys(
      appliedFilters[filterName.HOST_OS_FULL_VERSION]
    ).filter((key) => appliedFilters[filterName.HOST_OS_FULL_VERSION][key]);
    if (checkedOSVersions.length > 0) {
      filteredEntities = filteredEntities.filter((entity) =>
        checkedOSVersions.includes(entity.os_version)
      );
    }
  }

  // hostOSVersion
  if (filterName.HOST_OS_VERSION in appliedFilters) {
    for (const OSVersion of Object.keys(
      appliedFilters[filterName.HOST_OS_VERSION]
    )) {
      if (
        OSVersion !== ALL_OPTION_NAME &&
        !availableOSVersionsNames.includes(OSVersion)
      ) {
        delete appliedFilters[filterName.HOST_OS_VERSION][OSVersion];
      }
    }
  }
  if (
    filterName.HOST_OS_VERSION in appliedFilters &&
    Object.keys(appliedFilters[filterName.HOST_OS_VERSION]).length > 0
  ) {
    const checkedOSVersions = Object.keys(
      appliedFilters[filterName.HOST_OS_VERSION]
    ).filter((key) => appliedFilters[filterName.HOST_OS_VERSION][key]);
    if (checkedOSVersions.length > 0) {
      filteredEntities = filteredEntities.filter((entity) =>
        checkedOSVersions.includes(
          entity.platform === Platforms.WINDOWS
            ? getWindowsOSName(entity.os_version)
            : entity.platform === Platforms.MACOS
            ? getMacOSName(entity.os_version)
            : entity.os_version
        )
      );
    }
  }
  // Last seen
  if (
    filterName.HOST_LAST_SEEN in appliedFilters &&
    filterName.HOST_LAST_SEEN in filterAndOrLogics &&
    Object.keys(appliedFilters[filterName.HOST_LAST_SEEN]).length > 0
  )
    filteredEntities = filteredEntities.filter((entity) => {
      const lastSeenTimestamp = getHostLastSeenTimestamp(entity);
      if (!lastSeenTimestamp) return false;
      let selectedTimestamp = 0;
      for (const [name, info] of Object.entries(pastTimeValues)) {
        if (appliedFilters[filterName.HOST_LAST_SEEN][name])
          selectedTimestamp = getTimestampXDaysAgo(info.days);
      }
      if (
        filterAndOrLogics[filterName.HOST_LAST_SEEN] ===
        LogicNameFilter.TIME_AFTER
      )
        return lastSeenTimestamp > selectedTimestamp;
      else if (
        filterAndOrLogics[filterName.HOST_LAST_SEEN] ===
        LogicNameFilter.TIME_BEFORE
      )
        return lastSeenTimestamp < selectedTimestamp;
      else return false;
    });

  // Has Local Admin User
  if (Object.keys(appliedFilters[filterName.HAS_LOCAL_ADMIN_USER]).length > 0) {
    const checkedOptions = Object.keys(
      appliedFilters[filterName.HAS_LOCAL_ADMIN_USER]
    ).filter((key) => appliedFilters[filterName.HAS_LOCAL_ADMIN_USER][key]);

    if (checkedOptions.length > 0) {
      filteredEntities = filteredEntities.filter((entity) => {
        const hasLocalAdminUsers =
          Array.isArray(entity?.jamf_data?.local_user_admin) &&
          entity.jamf_data.local_user_admin.length > 0;

        const showWithLocalAdmins =
          checkedOptions.includes("true") && hasLocalAdminUsers;
        const showWithoutLocalAdmins =
          checkedOptions.includes("false") &&
          (!entity?.jamf_data?.local_user_admin ||
            entity.jamf_data.local_user_admin.length === 0);

        return showWithLocalAdmins || showWithoutLocalAdmins;
      });
    }
  }

  // CVE severity
  // Implemented in the sharedFiltersLogic

  entities.length = 0;
  for (let i = 0; i < filteredEntities.length; i++) {
    entities[i] = filteredEntities[i];
  }
};
